import { useState } from 'react';
import { useIsMutating } from '@tanstack/react-query';
import { BadgeCheck, CircleX, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { isSubscriptionError } from '@core/api/client';
import { useMe } from '@features/user-profile/hooks/useMe';
import type { MeSubscription, SubscriptionPlanState } from '@features/user-profile/hooks/useMe';
import {
  usePlans,
  useCreateCheckout,
  useChangePlan,
  useOpenPortal,
  useCancelSubscription,
  useResumeSubscription,
  useDeclineComp,
  useKeepComp,
  BILLING_REDIRECT_MUTATION_KEY,
  type Cadence,
  type Plan,
} from './api';
import { formatPlanDate } from './planDate';
import { BillingToggle } from './components/BillingToggle';
import { PlanCard } from './components/PlanCard';
import { CancelPlanDialog } from './components/CancelPlanDialog';
import { ContactSalesCard } from './components/ContactSalesCard';
import { NeedMoreCreditsBar } from './components/NeedMoreCreditsBar';

/**
 * Footer trust line — Journal marketing copy, kept as one place to edit (same rule as
 * `CONTACT_SALES_MAILTO` in `ContactSalesCard.tsx`).
 *
 * Each row is a 16px icon + label, in `--text-muted` (#617098) — the shape and colour the
 * 2026-08-16 Figma exports specify (each was a 16x16 slot followed by the label in that grey). They
 * previously rendered as bold `--text`, which competed with the plan cards for attention; a trust
 * line is reassurance, not a headline. Icons are lucide, per the repo's lucide-only rule.
 */
const FOOTER_TRUST_ITEMS = [
  { label: 'Cancel anytime', Icon: CircleX },
  { label: 'Secure checkout via Stripe', Icon: Lock },
  { label: 'No hidden fees', Icon: BadgeCheck },
] as const;

/** The tier a caller is on when nothing says otherwise — `core` IS the Free card. */
const DEFAULT_TIER: Plan['tier'] = 'core';

/**
 * The caller's tier, with no subscription row meaning the free tier rather than "unknown"
 * (2026-08-16). Used for BOTH the CTA label and the card's selected treatment, so the button and
 * the highlight can never disagree about which plan the user is on.
 */
export function currentTier(subscription: MeSubscription | null | undefined): Plan['tier'] {
  return subscription?.tier ?? DEFAULT_TIER;
}

/**
 * Whether `plan` is the one the caller is actually on — what the card renders as selected.
 *
 * A plain tier match is now correct for EVERY state, because `subscription.tier` is the server's
 * **effective** tier: a lapsed or past-due subscriber already reads `core`, so they highlight Free
 * (what they are using today) rather than the Pro they no longer hold, with no special case here.
 * A caller with no subscription row at all is on Free for the same reason.
 */
export function isCurrentPlan(plan: Plan, subscription: MeSubscription | null | undefined): boolean {
  return currentTier(subscription) === plan.tier;
}

interface CtaResolution {
  label: string;
  disabled: boolean;
  // `cancel`/`resume` are the STRIPE pair; `declineComp`/`keepComp` are the comp pair. They are never
  // interchangeable — a comp has no Stripe subscription behind it, so `/billing/cancel` 409s for one.
  action: 'checkout' | 'change' | 'portal' | 'cancel' | 'resume' | 'declineComp' | 'keepComp' | null;
}

const CURRENT_PLAN: CtaResolution = { label: 'Current plan', disabled: true, action: null };
const COMING_SOON: CtaResolution = { label: 'Coming soon', disabled: true, action: null };

/**
 * The Free card's CTA once a downgrade is already scheduled: disabled, and carrying the one fact the
 * "Scheduled" chip structurally can't — WHEN. Shared by both paths that schedule one (a Stripe
 * cancellation and a declined comp) so the two can never drift apart in wording; only the
 * no-date-on-the-wire fallback differs, since a comp has no billing cycle to name.
 */
function startsOnCta(endsAt: string | null | undefined, fallback: string): CtaResolution {
  const on = formatPlanDate(endsAt);
  return { label: on ? `Starts ${on}` : fallback, disabled: true, action: null };
}

/**
 * A plan card's CTA, as a table lookup on `(planState, plan.tier)` — 2026-08-16.
 *
 * **Why a table.** This function used to reconstruct the user's situation in the browser from
 * `source` + `status` + `isEntitled` + `isPurchasable`. Those four are not independent, and the
 * combination that broke was ordinary: a Pro user whose Pro did NOT come from Stripe emits
 * `source: 'manual'`, missed both stripe branches, and landed on the fallthrough — an **enabled
 * "Get started" on the plan they already held**, with a "Your plan" badge directly above it, while
 * the Free plan they'd fall back to read "Coming soon" (the user's report: *"If my plan is pro, why
 * is there a get started button that takes me to stripe? not logical"*). That button reached a
 * checkout guard which only blocks `Source == Stripe`, so it could genuinely buy a second
 * subscription for a tier they were already on.
 *
 * The server now resolves `planState` once, from the row + any active override + the clock, and the
 * browser only looks it up. Every arm below is total and mutually exclusive, so no combination can
 * fall through to a wrong default again.
 *
 * |                                    | Free card                     | Pro card                 |
 * |------------------------------------|-------------------------------|--------------------------|
 * | `free`                             | Current plan                  | Get started → checkout   |
 * | `proActive`                        | Switch to Free → cancel       | Current plan             |
 * | `proCancelling`                    | Starts {endsAt} (+chip)       | **Keep Pro** → resume    |
 * | `proPastDue`                       | Current plan                  | Update payment → portal  |
 * | `lapsed`                           | Current plan                  | Resubscribe → checkout   |
 * | `comped` + `canDeclineComp`        | Switch to Free → declineComp  | Current plan             |
 * | `comped` + `compDeclined`          | Starts {endsAt} (+chip)       | **Keep Pro** → keepComp  |
 * | `comped`, undated (neither flag)   | Managed by Optimus            | Current plan             |
 *
 * Rules the table encodes that the old boolean algebra could not:
 *  - **Cancelling never removes anything.** `proCancelling` is still Pro — the Pro card stays
 *    selected and offers the undo, per the user's *"cancels and then regrets after 1 week"* case.
 *  - **Downgrading to Free is a cancellation, not a purchase**, so the Free card never consults
 *    `isPurchasable` to decide whether it can be reached.
 *  - **A declined comp is the same SHAPE as `proCancelling`, driven by a different pair of verbs**
 *    (2026-08-16). Declining does not shorten the comp: `planState` stays `comped` and `endsAt` is
 *    untouched, so the last three rows are one state read through two server-computed flags rather
 *    than three. What differs is only which endpoint the button hits — a comp has no Stripe
 *    subscription, and `/billing/cancel` 409s for one.
 *  - **An UNDATED comp is not declinable.** With `expires_at IS NULL` there is no date to fall to
 *    Free on, so `canDeclineComp` is false server-side and the card keeps its original copy.
 */
export function resolveCta(plan: Plan, subscription: MeSubscription | null | undefined): CtaResolution {
  const state: SubscriptionPlanState = subscription?.planState ?? 'free';

  if (plan.tier === 'pro') {
    switch (state) {
      case 'proActive':
        return CURRENT_PLAN;
      // A declined comp is still Pro until the comp's own `endsAt`, so the undo belongs on the card
      // the user is still on — exactly like `proCancelling`, but against the comp endpoints.
      case 'comped':
        return subscription?.compDeclined
          ? { label: 'Keep Pro', disabled: false, action: 'keepComp' }
          : CURRENT_PLAN;
      // Still Pro, and reversible until `endsAt` — the single click that undoes the cancellation.
      case 'proCancelling':
        return { label: 'Keep Pro', disabled: false, action: 'resume' };
      // Stripe is still retrying the card; a NEW checkout is refused by the BE's `already_subscribed`
      // guard while the subscription is Active/PastDue, so the card on file is what to fix.
      case 'proPastDue':
        return { label: 'Update payment method', disabled: false, action: 'portal' };
      case 'lapsed':
        return plan.isPurchasable
          ? { label: 'Resubscribe', disabled: false, action: 'checkout' }
          : COMING_SOON;
      case 'free': {
        if (!plan.isPurchasable) return COMING_SOON;
        // A live Stripe subscription is MODIFIED, never re-bought — buying twice is how one customer
        // ends up with two subscriptions billing forever. It also changes the word: someone already
        // paying is upgrading, someone who has never paid is starting.
        const live = subscription?.hasLiveStripeSubscription ?? false;
        return { label: live ? 'Upgrade' : 'Get started', disabled: false, action: live ? 'change' : 'checkout' };
      }
    }
  }

  switch (state) {
    // Downgrading to Free is a CANCELLATION, not a purchase — which is why this card must never ask
    // `isPurchasable` (it has no Stripe price, so it read "Coming soon" to the very Pro subscriber
    // who was falling back to it — the 2026-08-16 report).
    case 'proActive':
      return { label: 'Switch to Free', disabled: false, action: 'cancel' };
    // Already scheduled — the chip in the badge slot says so. The button therefore carries the fact
    // the chip can't: WHEN. (It read "Scheduled" too, which put the same word twice on one card.)
    case 'proCancelling':
      return startsOnCta(subscription?.endsAt, 'Starts next cycle');
    case 'comped':
      // Declined: identical to `proCancelling` on this card — Free is where they land, at the comp's
      // OWN expiry, which declining did not move.
      if (subscription?.compDeclined) return startsOnCta(subscription?.endsAt, 'Starts when your comp ends');
      // Declinable (dated, active, not yet declined): the same words as a paid downgrade, because it
      // is the same decision — Pro runs out on the date it always would, then Free. It reaches
      // `/billing/comp/decline`, never `/billing/cancel`, which has no Stripe subscription to cancel.
      if (subscription?.canDeclineComp) return { label: 'Switch to Free', disabled: false, action: 'declineComp' };
      // An UNDATED comp: never expires, so there is no date to fall to Free on and nothing to
      // decline. Saying who owns the decision beats a dead button that always errors (the BE answers
      // `subscriptions.comp_not_declinable` here).
      return { label: 'Managed by Optimus', disabled: true, action: null };
    // free / lapsed / proPastDue all sit on the Core floor right now — Core IS what they are using.
    default:
      // Preserved from the pre-`planState` resolver: should Free/Core ever gain a real Stripe price,
      // a grandfathered free user self-migrates via checkout rather than being stuck behind a
      // disabled "Current plan."
      return plan.isPurchasable && state === 'free'
        ? { label: 'Get started', disabled: false, action: 'checkout' }
        : CURRENT_PLAN;
  }
}

/**
 * The neutral chip for a card that is not the current plan but is about to be: during a pending
 * downgrade the Free card is where the user lands at `endsAt`. Two ways to get there, one chip — a
 * cancelled Stripe subscription (`proCancelling`) and a declined comp (`comped` + `compDeclined`,
 * 2026-08-16), which are the same fact about the future from this card's point of view.
 */
function resolveChip(plan: Plan, subscription: MeSubscription | null | undefined): string | undefined {
  if (plan.tier !== 'core' || !subscription) return undefined;
  const scheduled =
    subscription.planState === 'proCancelling' ||
    (subscription.planState === 'comped' && subscription.compDeclined);
  return scheduled ? 'Scheduled' : undefined;
}

/**
 * The one line of prose that states what happens next and when — the fact a pricing card structurally
 * can't carry, and the one a user checks after clicking cancel. Null when there is nothing pending.
 */
function planStatusLine(subscription: MeSubscription | null | undefined): string | null {
  if (!subscription) return null;
  const ends = formatPlanDate(subscription.endsAt);
  const renews = formatPlanDate(subscription.renewsAt);
  switch (subscription.planState) {
    case 'proCancelling':
      return ends
        ? `Pro ends ${ends}. You'll move to Free then. Keep Pro to undo.`
        : "Pro ends at the end of your current billing period. You'll move to Free then.";
    case 'proActive':
      return renews ? `Pro renews ${renews}.` : null;
    case 'proPastDue':
      return "We couldn't charge your card. You're on Free until the payment goes through.";
    case 'lapsed':
      return "Your Pro subscription has ended. You're on Free. Your journal and history are all still here.";
    case 'comped':
      // Declining does NOT bring the end date forward, so this line's job is to say that out loud:
      // the same date as before, now with Free after it. Plus the undo, stated before it's needed.
      if (subscription.compDeclined) {
        return ends
          ? `You've declined the comp. Pro still runs until ${ends}. You'll move to Free then. Keep Pro to undo.`
          : "You've declined the comp. Pro runs to the end of the comp. You'll move to Free then. Keep Pro to undo.";
      }
      return ends ? `Pro is comped for your account until ${ends}.` : 'Pro is comped for your account.';
    default:
      return null;
  }
}

/** Friendly copy for a failed billing mutation (I1). `isSubscriptionError` classifies a 403 the same
 * way a stale gated-data read would be classified elsewhere in the app (`core/api/client.ts`) — any
 * other failure (network, timeout, an unrelated 4xx/5xx) gets a generic retry message rather than a
 * guess at what went wrong. */
function mutationErrorMessage(err: unknown): string {
  const kind = isSubscriptionError(err);
  if (kind === 'inactive') return "Your subscription isn't active right now. Refresh the page and try again.";
  if (kind === 'tierRequired') return "This plan isn't available for your account yet. Refresh the page and try again.";
  return "Something went wrong. Please try again.";
}

/**
 * The annual toggle's `SAVE {pct}%` chip is quoted off the featured plan (Pro) — the only plan a
 * savings hook is meaningful for. The free tier's $0 price would either divide-by-zero or read as
 * a nonsensical 100% discount, so it's never the reference plan. Hidden whenever that reference
 * plan doesn't have both a monthly AND an annual price to compare (unpriced, or a misconfigured
 * catalog row).
 */
function computeSavePct(plans: Plan[]): number | null {
  const reference = plans.find(p => p.isFeatured) ?? plans.find(p => p.monthlyPriceCents > 0);
  if (!reference || reference.monthlyPriceCents <= 0 || reference.annualPriceCents <= 0) return null;
  return Math.round((1 - reference.annualPriceCents / (12 * reference.monthlyPriceCents)) * 100);
}

interface SubscribePageProps {
  /**
   * True for the full-bleed takeover (`fixed inset-0 z-30`) — used ONLY where no `AppShell` exists
   * to frame the page, i.e. `SubscriptionGuard` rendering this in AppShell's own place for an
   * unentitled user. Default `false` renders as normal routed page content (no fixed positioning,
   * no z-index hack): it fills the routed content area with its own `bg-canvas` container, the
   * sidebar/topbar stay visible, and the page scrolls with the document like any other page — the
   * `/upgrade` route (reachable for an already-entitled user via ProfileMenu/nav-lock/ProUpsell)
   * uses this framed variant per the approved Figma (2026-08-13 design correction; supersedes the
   * earlier "renders full-screen either way" note that used to sit on that route).
   */
  standalone?: boolean;
}

export function SubscribePage({ standalone = false }: SubscribePageProps) {
  const [cadence, setCadence] = useState<Cadence>('monthly');
  // WHICH downgrade the confirm dialog is confirming, not just whether it's open. The two paths share
  // one dialog (see `CancelPlanDialog` — the copy is true of both) but never one mutation: a comp has
  // no Stripe subscription behind it, so `/billing/cancel` would 409 for a comped caller and
  // `/billing/comp/decline` has nothing to do for a paying one.
  const [confirming, setConfirming] = useState<'subscription' | 'comp' | null>(null);
  const plansQuery = usePlans();
  const me = useMe();
  const createCheckout = useCreateCheckout();
  const changePlan = useChangePlan();
  const openPortal = useOpenPortal();
  const cancelSubscription = useCancelSubscription();
  const resumeSubscription = useResumeSubscription();
  const declineComp = useDeclineComp();
  const keepComp = useKeepComp();

  const plans = plansQuery.data ?? [];
  const subscription = me.data?.subscription;
  const savePct = computeSavePct(plans);
  // Server-computed: a real Stripe customer exists behind this row. Asking `source === 'stripe'`
  // showed the link to comped/manual users, for whom the portal call 409s.
  const showManageBilling = subscription?.manageBillingAvailable ?? false;
  const statusLine = planStatusLine(subscription);

  // I1: disable every CTA (and Manage billing) while ANY billing mutation is in flight — the clicked
  // one obviously must not fire twice, and this also rules out a second, DIFFERENT card being
  // clicked mid-redirect/mid-request. `useIsMutating` covers the two redirecting mutations even when
  // a sibling component started them.
  const redirecting = useIsMutating({ mutationKey: BILLING_REDIRECT_MUTATION_KEY }) > 0;
  const mutationPending =
    redirecting ||
    createCheckout.isPending ||
    changePlan.isPending ||
    openPortal.isPending ||
    cancelSubscription.isPending ||
    resumeSubscription.isPending ||
    declineComp.isPending ||
    keepComp.isPending;
  const onMutationError = (err: unknown) => toast.error(mutationErrorMessage(err));

  // Both downgrades end the same way for the user (Pro to its existing end date, then Free), so they
  // share the dialog, the close-on-settle handling and the error toast — only the endpoint and the
  // "when" wording differ. The BE answers a double-submit with a 409 rather than a silent no-op, so
  // the dialog stays locked while this is in flight and the refreshed `['me']` settles the truth.
  const confirmDowngrade = () => {
    const comp = confirming === 'comp';
    const mutation = comp ? declineComp : cancelSubscription;
    const message = comp
      ? "Switched to Free when your comp ends. You keep Pro until then."
      : 'Switched to Free at the end of your billing period.';
    mutation.mutate(undefined, {
      onSuccess: () => {
        setConfirming(null);
        toast.success(message);
      },
      onError: err => {
        setConfirming(null);
        onMutationError(err);
      },
    });
  };

  // Standalone (paywall takeover, no shell to frame it): full-bleed fixed overlay above everything.
  // Framed (`/upgrade` route, inside AppShell): a normal block that fills the routed content area
  // and scrolls with the document — AppShell's `<main>` already owns the scroll container.
  const rootClassName = standalone
    ? 'fixed inset-0 z-30 overflow-y-auto bg-canvas text-text'
    : 'w-full bg-canvas text-text';

  return (
    <div data-testid="subscribe-page" className={rootClassName}>
      {/* Sized to fit WITHOUT vertical scroll (2026-08-16 user request: "everything should fit
          without scroll"). py-16 alone cost 128px of the viewport before the heading, and the
          gaps below compounded it — this page is three cards and a strapline, so it should never
          have needed scrolling on a laptop. Padding scales back up on tall viewports (lg). */}
      <div className="mx-auto flex max-w-[1361px] flex-col items-center gap-6 px-6 py-8 lg:py-10">
        <div className="flex flex-col items-center gap-4">
          <div className="text-center">
            <h1 className="text-[32px] font-semibold leading-[40px] text-text lg:text-[40px] lg:leading-[48px]">Choose your plan</h1>
            <p className="mt-2 text-[13px] leading-[20px] text-text-muted">
              Simple pricing that scales with your trading.
            </p>
          </div>

          <BillingToggle cadence={cadence} onChange={setCadence} savePct={savePct} />

          {statusLine && (
            <p data-testid="plan-status-line" className="text-center text-[13px] leading-[20px] text-text-muted">
              {statusLine}
            </p>
          )}

          {showManageBilling && (
            <button
              type="button"
              disabled={mutationPending}
              onClick={() => openPortal.mutate(undefined, { onError: onMutationError })}
              className="text-[13px] text-text-muted underline underline-offset-4 hover:text-text disabled:cursor-not-allowed disabled:opacity-50"
            >
              Manage billing
            </button>
          )}
        </div>

        {plansQuery.isPending ? (
          <p className="text-text-muted">Loading plans…</p>
        ) : plansQuery.isError ? (
          <p className="text-text-muted">Couldn&rsquo;t load plans. Please try again.</p>
        ) : (
          // Figma "Cards": `padding: 0 48px; gap: 24px`. Items STRETCH (the default) so Free and
          // Custom fill the row and stay equal; the featured card opts out with a fixed width and
          // `-my-3`, which is how it ends up 24px taller and centred across the row edge without
          // dragging its siblings up with it. See PlanCard for why negative margin, not height.
          <div className="flex w-full flex-col gap-6 lg:flex-row lg:px-12">
            {plans.map(plan => {
              const cta = resolveCta(plan, subscription);
              return (
                <PlanCard
                  key={plan.tier}
                  plan={plan}
                  cadence={cadence}
                  isCurrent={isCurrentPlan(plan, subscription)}
                  chip={resolveChip(plan, subscription)}
                  cta={{
                    label: cta.label,
                    disabled: cta.disabled || mutationPending,
                    onClick:
                      cta.action == null
                        ? undefined
                        : () => {
                            const body = { tier: plan.tier, cadence };
                            if (cta.action === 'checkout') createCheckout.mutate(body, { onError: onMutationError });
                            else if (cta.action === 'change') changePlan.mutate(body, { onError: onMutationError });
                            else if (cta.action === 'portal') openPortal.mutate(undefined, { onError: onMutationError });
                            // Confirm first: it ends a paid plan, and the two facts that matter
                            // (Pro runs to `endsAt`; nothing is deleted) are only in the dialog.
                            else if (cta.action === 'cancel') setConfirming('subscription');
                            // Same confirm, same two facts — a comp is given rather than bought, but
                            // declining it is still "Pro until the date it already had, then Free".
                            else if (cta.action === 'declineComp') setConfirming('comp');
                            else if (cta.action === 'keepComp') keepComp.mutate(undefined, { onError: onMutationError });
                            else resumeSubscription.mutate(undefined, { onError: onMutationError });
                          },
                  }}
                />
              );
            })}
            <ContactSalesCard cadence={cadence} />
          </div>
        )}

        {/* Under the cards, per the 2026-08-16 Figma: a subscription tops up monthly, a pack tops up
            now, and this is where someone comparing plans asks for the latter. */}
        <NeedMoreCreditsBar />

        <div className="flex flex-wrap items-center justify-center gap-8 text-center">
          {FOOTER_TRUST_ITEMS.map(({ label, Icon }) => (
            <span key={label} className="flex items-center gap-2 text-base font-bold leading-[19px] text-text-muted">
              <Icon className="h-4 w-4 shrink-0" aria-hidden />
              {label}
            </span>
          ))}
        </div>
      </div>

      <CancelPlanDialog
        open={confirming !== null}
        onClose={() => setConfirming(null)}
        onConfirm={confirmDowngrade}
        pending={confirming === 'comp' ? declineComp.isPending : cancelSubscription.isPending}
        // What Pro actually runs until, for BOTH paths the dialog opens from, with no branch needed:
        // `renewsAt` while `proActive`, and a comp has `renewsAt: null` + `endsAt` set to its own
        // (unchanged, never shortened by declining) `expires_at`.
        endsAt={subscription?.renewsAt ?? subscription?.endsAt ?? null}
      />
    </div>
  );
}
